import { computed, ref } from 'vue'
import { useSessionStorage } from '@vueuse/core'
import { z } from 'zod'

import { defaultAmount } from '~/basics/const'
import type { SimulationProposalType, SimulationResponseType } from '~/extensions/simulator/simulator.type'
import { useI18n } from 'vue-i18n'

/**
 * Simulator steps list
 */
const steps = ['offers', 'amount', 'duration', 'proposals'] as const
const currentStep = ref<(typeof steps)[number] | null>(null)

/**
 * Amount step props
 */
export const amountProps = ref({
  min: 100,
  max: 80000,
})

/**
 * Duration step props
 */
export const durationProps = ref({
  min: 6,
  max: 36,
  step: 6,
})

export const amountSliderProps = computed(() => {
  return {
    min: 0,
    max: possibleDurations.value.length - 1,
    step: 1,
  }
})

export const possibleDurations = ref<number[]>([])
export const possibleAmounts = ref<number[]>([])
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const amountByDuration = ref<any>({})
export const amountSliderValue = ref([1])

const onDurationChange = (value?: number[]) => {
  if (value) {
    amountSliderValue.value = [amountSliderProps.value.max - possibleDurations.value.indexOf(value[0])]
  }
}

const onAmountChange = (value?: number[]) => {
  if (value) {
    data.value.duration = [possibleDurations.value[possibleDurations.value.length - 1 - value[0]]]
  }
}

// Fixe
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildFormSchema = (t: any) =>
  z.object({
    timestamp: z.number(),
    offers: z.object({
      offer: z.string().min(1, { message: t('simulator.offersStep.offer.error') }),
      subOffer: z.string().min(1, { message: t('simulator.offersStep.subOffer.error') }),
    }),
    amount: z
      .number({ message: t('simulator.amountStep.errors.number') })
      .min(amountProps.value.min, { message: t('simulator.amountStep.errors.min', { min: amountProps.value.min }) })
      .max(amountProps.value.max, { message: t('simulator.amountStep.errors.max', { max: amountProps.value.max }) })
      .refine(
        (amount) => amount && amount >= amountProps.value.min,
        t('simulator.amountStep.errors.range', { min: amountProps.value.min, max: amountProps.value.max }),
      ),
    duration: z
      .array(
        z
          .number()
          .min(durationProps.value.min, {
            message: t('simulator.durationStep.errors.min', { min: durationProps.value.min }),
          })
          .max(durationProps.value.max, {
            message: t('simulator.durationStep.errors.max', { max: durationProps.value.max }),
          }),
      )
      .min(1, { message: t('simulator.durationStep.errors.null') }),
  })

export type formSchemaType = z.infer<ReturnType<typeof buildFormSchema>>

const defaultData: formSchemaType = {
  timestamp: Date.now(),
  offers: {
    offer: '',
    subOffer: '',
  },
  amount: defaultAmount,
  duration: [durationProps.value.min],
}

const data = useSessionStorage('simulator', defaultData)

interface SimulatorQueryParams {
  offer: string
  subOffer: string
  amount: number
  duration: number[]
}

const selectedOfferLabel = ref<string>('')
const loading = ref<boolean>(false)
const simulationResponse = ref<SimulationResponseType | null>(null)
const defaultSelectedProposal: SimulationProposalType = {
  id: '',
  borrowers: [],
  depositAmount: 0,
  purchaseAmount: 0,
  numberOfPayments: 0,
  scaleId: '',
  scaleTypeCode: '',
  contractPeriod: 0,
  annualPercentageRate: 0.0,
  legalNotices: '',
  legalNoticesHtml: '',
  nominalRate: 0.0,
  fixedBorrowingRate: 0.0,
  fees: 0,
  sellersCost: 0,
  loanAmount: 0,
  monthlyPaymentNoInsurance: 0.0,
  monthlyPaymentWithInsurance: 0.0,
  totalInterestAmount: 0.0,
  insuranceAnnualPercentageRate: 0.0,
  totalAmountWithInsurance: 0.0,
  totalAmountNoInsurance: 0.0,
}
const selectedProposal = useSessionStorage('selectedProposal', defaultSelectedProposal)

export const useSimulatorData = () => {
  const { t } = useI18n()

  const formSchema = computed(() => buildFormSchema(t))

  const resetForm = (params: SimulatorQueryParams) => {
    data.value = {
      ...defaultData,
      offers: {
        offer: params.offer ? params.offer : defaultData.offers.offer,
        subOffer: params.subOffer ? params.subOffer : defaultData.offers.subOffer,
      },
      amount: params.amount ? Number(params.amount) : defaultData.amount,
      duration: params.duration ? [Number(params.duration)] : defaultData.duration,
    }
  }

  return {
    amountByDuration,
    amountProps,
    amountSliderValue,
    buildFormSchema,
    currentStep,
    data,
    durationProps,
    formSchema,
    loading,
    onAmountChange,
    onDurationChange,
    possibleAmounts,
    resetForm,
    selectedOfferLabel,
    selectedProposal,
    simulationResponse,
    amountSliderProps,
    steps,
  }
}
